import { changeEduStatus } from "../../../../utilis/appStatus";
import { formatDisplayDate } from "../commissionsConstants";
import {
  formatAppBadge,
  formatClientId,
  formatCurrency,
  formatPercent,
  getInitials,
  resolveIntakeLabel,
} from "./commissionDetailConstants";
import { normalizeSavedCommissionPlan } from "./commissionPlanUtils";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text && text !== "—") return text;
  }
  return "";
}

function resolveStatusTitle(statusCode) {
  const match = changeEduStatus.find(
    (s) => String(s.value) === String(statusCode),
  );
  return match?.title || "Unknown Status";
}

function normalizeApplication(raw = {}) {
  const appId = raw.app_id ?? raw.id ?? null;
  return {
    ...raw,
    id: appId,
    intakeid: raw.intakeid ?? raw.intake_id ?? null,
    intake_name: raw.intake_name ?? raw.intakeName ?? null,
    statuschangedate: raw.statuschangedate ?? raw.status_change_date ?? null,
  };
}

function resolveEduAppCourse(eduappcourses = [], courseId, applicationCourseId) {
  if (!Array.isArray(eduappcourses) || !eduappcourses.length) return {};

  if (applicationCourseId != null && applicationCourseId !== "") {
    const byAppCourse = eduappcourses.find(
      (row) => String(row.id) === String(applicationCourseId),
    );
    if (byAppCourse) return byAppCourse;
  }

  if (courseId != null && courseId !== "") {
    const match = eduappcourses.find(
      (row) =>
        String(row.courseid ?? row.course_id ?? row.courseId) ===
        String(courseId),
    );
    if (match) return match;
  }
  return eduappcourses[0] || {};
}

function resolveCommissionableTuitionFee(eduCourse = {}, course = {}) {
  const fromApp = toNumber(
    eduCourse.commissionable_tuition_fee ??
      eduCourse.commissionableTuitionFee ??
      eduCourse.commissionable_fee ??
      eduCourse.commissionableFee,
  );
  if (fromApp > 0) return fromApp;

  return toNumber(
    course.commissionable_tuition_fee ??
      course.commissionableTuitionFee ??
      course.commissionable_fee ??
      course.commissionableFee,
  );
}

function resolveTuitionFee(eduCourse = {}, course = {}) {
  // 1) eduappcourse.offered_fee first, then other eduappcourse fee fields
  const fromApp = toNumber(
    eduCourse.offered_fee ??
      eduCourse.offeredFee ??
      eduCourse.tution_fee ??
      eduCourse.tuition_fee ??
      eduCourse.tuitionFee ??
      eduCourse.fee ??
      eduCourse.course_fee,
  );
  if (fromApp > 0) return fromApp;

  // 2) fallback to course catalog fee
  return toNumber(
    course.offered_fee ??
      course.offeredFee ??
      course.tution_fee ??
      course.tuition_fee ??
      course.tuitionFee,
  );
}

function resolveCourseDates(eduCourse = {}, course = {}) {
  return {
    startDate: firstNonEmpty(
      eduCourse.startdate,
      eduCourse.start_date,
      eduCourse.course_start,
      eduCourse.coursestart,
      eduCourse.start,
      course.startdate,
      course.start_date,
      course.course_start,
    ),
    endDate: firstNonEmpty(
      eduCourse.enddate,
      eduCourse.end_date,
      eduCourse.course_end,
      eduCourse.courseend,
      eduCourse.end,
      course.enddate,
      course.end_date,
      course.course_end,
    ),
  };
}

function normalizeInstallmentStatus(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "paid") return "Paid";
  if (value === "overdue") return "Overdue";
  return "Pending";
}

function resolvePaymentType(type) {
  const map = { 0: "Commission", 1: "GST", 2: "Bonus" };
  return map[Number(type)] || "Commission";
}

function mapInstallmentRow(row = {}) {
  const commissionExcl = toNumber(row.commissionexcl);
  const commissionIncl = toNumber(row.commissionincl);
  const gst = Math.max(0, commissionIncl - commissionExcl);

  return {
    id: row.id,
    number: row.installment_no ?? row.id,
    dueDate: row.duedate || row.due_date || "",
    amount: toNumber(row.amount),
    commission: commissionIncl || commissionExcl,
    gst,
    status: normalizeInstallmentStatus(row.status),
    remaining: toNumber(row.remaining_amount),
  };
}

function mapPaymentRow(row = {}) {
  const docUrl = String(row.document_url || "");
  const receipt = docUrl ? docUrl.split("/").pop() : "—";

  return {
    id: row.id,
    date: row.paymentdate || row.payment_date || "",
    amount: toNumber(row.amount),
    type: resolvePaymentType(row.type),
    receipt,
    recordedBy: row.user || row.recorded_by || "Staff",
    recordedByInitials: getInitials(row.user || row.recorded_by || "Staff"),
  };
}

function mapCommissionNoteRow(row = {}, index = 0) {
  return {
    id: row.id ?? index + 1,
    author: row.user || "Staff",
    authorInitials: getInitials(row.user || "Staff"),
    date: formatDisplayDate(
      String(row.created_at || row.updated_at || "").slice(0, 10),
    ),
    text: row.notes || row.note || "",
  };
}

function sumCommissionHistory(commissions = []) {
  return commissions.reduce((total, commission) => {
    const history = Array.isArray(commission.history) ? commission.history : [];
    return (
      total + history.reduce((sum, entry) => sum + toNumber(entry.amount), 0)
    );
  }, 0);
}

function resolveExpectedBonus({ eduappcourses = [], courseBonus = [] }) {
  const eduCourse = eduappcourses[0] || {};
  const appBonus = toNumber(eduCourse.app_bonus);
  if (appBonus > 0) return appBonus;

  const newAppBonus = toNumber(eduCourse.new_app_bonus);
  if (newAppBonus > 0) return newAppBonus;

  return (Array.isArray(courseBonus) ? courseBonus : []).reduce(
    (sum, row) => sum + toNumber(row.amount ?? row.bonus_amount),
    0,
  );
}

function sumBonusReceived(bonusInstallments = []) {
  return (Array.isArray(bonusInstallments) ? bonusInstallments : []).reduce(
    (sum, row) => {
      const status = String(row.status || "").toLowerCase();
      if (status === "paid") {
        return sum + toNumber(row.amount);
      }
      return sum;
    },
    0,
  );
}

function resolveBonusStatus({ expected, received }) {
  if (expected <= 0 && received <= 0) return "NOT APPLICABLE";
  if (received <= 0) return "NOT RECEIVED";
  if (received >= expected) return "FULLY RECEIVED";
  return "PARTIALLY RECEIVED";
}

function resolveProgressPercent(part, total) {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, (part / total) * 100));
}

export function mapCommissionDetailViewModel(apiData = {}, options = {}) {
  const {
    intakes = [],
    courseId = null,
    applicationCourseId = null,
  } = options;

  const client = apiData.client || {};
  const student = apiData.student || client || {};
  const cnic =
    apiData.client_cnic ||
    apiData.cnic ||
    client.client_cnic ||
    client.cnic ||
    student.client_cnic ||
    student.cnic ||
    {};
  const provider = apiData.provider || {};
  const course = apiData.course || {};
  const providerCommission = apiData.provider_commission || {};
  const application = normalizeApplication(apiData.application || {});
  const eduappcourses = Array.isArray(apiData.eduappcourses)
    ? apiData.eduappcourses
    : Array.isArray(apiData.edu_app_courses)
      ? apiData.edu_app_courses
      : Array.isArray(apiData.appeducourses)
        ? apiData.appeducourses
        : [];
  const resolvedApplicationCourseId =
    applicationCourseId ??
    application.application_course_id ??
    application.applicationCourseId ??
    null;
  const resolvedCourseId =
    courseId ?? application.courseid ?? application.course_id ?? course.id ?? null;
  const eduAppCourse = resolveEduAppCourse(
    eduappcourses,
    resolvedCourseId,
    resolvedApplicationCourseId,
  );
  const courseDates = resolveCourseDates(eduAppCourse, course);
  const commissions = Array.isArray(apiData.commissions)
    ? apiData.commissions
    : [];
  const commissionNotes = Array.isArray(apiData.commission_notes)
    ? apiData.commission_notes
    : [];
  const courseBonus = Array.isArray(apiData.course_bonus)
    ? apiData.course_bonus
    : [];
  const bonusInstallments = Array.isArray(apiData.bonus_installment)
    ? apiData.bonus_installment
    : [];

  const currency = "AUD";
  const statusTitle = resolveStatusTitle(application.status);
  const applicationId = application.id;

  const clientIdRaw =
    student.id ?? client.id ?? student.client_id ?? client.client_id ?? null;
  // Student ID is client.student_id only — never fall back to client.id
  const displayStudentId = firstNonEmpty(
    client.student_id,
    student.student_id,
    client.studentId,
    student.studentId,
  );
  const clientName = firstNonEmpty(
    student.name,
    client.name,
    student.full_name,
    client.full_name,
    student.client_name,
    client.client_name,
  );
  const birthDate = firstNonEmpty(
    cnic.dob,
    cnic.date_of_birth,
    cnic.birth_date,
    student.dob,
    client.dob,
  );

  const courseTuitionFee = resolveTuitionFee(eduAppCourse, course);
  const courseCommissionableTuitionFee = resolveCommissionableTuitionFee(
    eduAppCourse,
    course,
  );
  const paidTuitionFee = 0;
  const remainingTuitionFee = courseTuitionFee;

  const providerCommissionOnshore = toNumber(
    providerCommission.onshore_commission,
  );
  const providerCommissionOffshore = toNumber(
    providerCommission.offshore_commission,
  );

  const installments = commissions.map(mapInstallmentRow);
  const expectedCommission = installments.reduce(
    (sum, row) => sum + row.commission,
    0,
  );
  const outstandingCommission = installments.reduce(
    (sum, row) => sum + row.remaining,
    0,
  );
  const commissionReceived = sumCommissionHistory(commissions);

  const payments = commissions
    .flatMap((commission) =>
      (Array.isArray(commission.history) ? commission.history : []).map(
        mapPaymentRow,
      ),
    )
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const expectedBonus = resolveExpectedBonus({ eduappcourses, courseBonus });
  const bonusReceived = sumBonusReceived(bonusInstallments);
  const outstandingBonus = Math.max(0, expectedBonus - bonusReceived);
  const bonusStatus = resolveBonusStatus({
    expected: expectedBonus,
    received: bonusReceived,
  });

  const intakeLabel =
    application.intake_name ||
    resolveIntakeLabel(application.intakeid, intakes) ||
    "—";

  const courseName = firstNonEmpty(
    eduAppCourse.coursename,
    eduAppCourse.course_name,
    eduAppCourse.name,
    course.name,
  );

  const planApplicationCourseId =
    eduAppCourse.id ??
    eduAppCourse.application_course_id ??
    eduAppCourse.app_course_id ??
    eduAppCourse.eduappcourse_id ??
    resolvedApplicationCourseId ??
    null;

  const levelObj =
    (apiData.level && typeof apiData.level === "object"
      ? apiData.level
      : null) ||
    (course.level && typeof course.level === "object" ? course.level : null) ||
    (eduAppCourse.level && typeof eduAppCourse.level === "object"
      ? eduAppCourse.level
      : null) ||
    {};

  const levelId =
    levelObj.id ??
    course.level_id ??
    course.levelid ??
    eduAppCourse.level_id ??
    eduAppCourse.levelid ??
    null;

  const levelTitle = firstNonEmpty(
    levelObj.title,
    levelObj.name,
    levelObj.level_title,
    course.level_title,
    course.level_name,
    typeof course.level === "string" ? course.level : "",
    eduAppCourse.level_title,
    eduAppCourse.level_name,
    typeof eduAppCourse.level === "string" ? eduAppCourse.level : "",
  );

  const onshoreRate = toNumber(
    course.onshore_com ??
      eduAppCourse.onshore_com ??
      providerCommission.onshore_commission,
  );
  const offshoreRate = toNumber(
    course.offshore_com ??
      eduAppCourse.offshore_com ??
      providerCommission.offshore_commission,
  );

  const applicationLocation = String(
    application.location || apiData.location || "",
  )
    .trim()
    .toLowerCase();
  const location =
    applicationLocation === "onshore" || applicationLocation === "offshore"
      ? applicationLocation
      : "offshore";

  const existingPlanRaw =
    apiData.new_commission_plan ||
    apiData.commission_plan ||
    (Array.isArray(apiData.new_commission_plans)
      ? apiData.new_commission_plans[0]
      : null) ||
    null;

  const existingPlan = normalizeSavedCommissionPlan(existingPlanRaw, {
    location,
    tuitionFee: courseTuitionFee,
    currency,
    levelId,
    applicationCourseId: planApplicationCourseId,
  });

  const tuitionProgressPct = resolveProgressPercent(
    paidTuitionFee,
    courseTuitionFee,
  );
  const commissionProgressPct = resolveProgressPercent(
    commissionReceived,
    expectedCommission,
  );

  return {
    student: {
      id: clientIdRaw,
      name: clientName || "—",
      email: firstNonEmpty(student.email, client.email) || "—",
      mobile: firstNonEmpty(student.mobile, client.mobile) || "—",
      initials: getInitials(clientName),
      clientId: clientIdRaw != null ? String(clientIdRaw) : "",
      clientIdFormatted: formatClientId(clientIdRaw),
      studentId: displayStudentId,
      birthDate,
    },
    provider: {
      id: provider.id,
      name: provider.name || "—",
      email: provider.email || "",
      city: provider.city,
      campus: provider.city,
      country: provider.country,
      address: provider.address || "",
      personDetails: Array.isArray(provider.person_details)
        ? provider.person_details
        : Array.isArray(provider.personDetails)
          ? provider.personDetails
          : [],
    },
    course: {
      id: course.id ?? eduAppCourse.courseid ?? eduAppCourse.course_id,
      applicationCourseId: planApplicationCourseId,
      levelId,
      levelTitle: levelTitle || "—",
      name: courseName || "—",
      duration: course.duration ?? eduAppCourse.duration,
      tuitionFee: courseTuitionFee,
      startDate: courseDates.startDate,
      endDate: courseDates.endDate,
      onshoreCommission: onshoreRate,
      offshoreCommission: offshoreRate,
      onshoreDiscount: toNumber(course.onshore_dis ?? eduAppCourse.onshore_dis),
      offshoreDiscount: toNumber(
        course.offshore_dis ?? eduAppCourse.offshore_dis,
      ),
      onshoreBonus: toNumber(
        course.onshore_bonus ?? eduAppCourse.onshore_bonus,
      ),
      offshoreBonus: toNumber(
        course.offshore_bonus ?? eduAppCourse.offshore_bonus,
      ),
    },
    application: {
      id: applicationId,
      badge: formatAppBadge(applicationId, application.created_at),
      statusTitle,
      statusCode: application.status,
      intakeLabel,
      location,
      statusChangedDate: formatDisplayDate(application.statuschangedate),
      startDate: courseDates.startDate,
      endDate: courseDates.endDate,
    },
    inquiry: {
      providerName: provider.name && provider.name !== "—" ? provider.name : "",
      studentId: displayStudentId,
      clientName,
      studentBirthDate: birthDate,
      course: courseName,
      start: courseDates.startDate,
      end: courseDates.endDate,
      intake: intakeLabel !== "—" ? intakeLabel : "",
      tuitionFee: courseTuitionFee > 0 ? String(courseTuitionFee) : "",
      commissionable_tuition_fee:
        courseCommissionableTuitionFee > 0
          ? String(courseCommissionableTuitionFee)
          : "",
      applicationCourseId: planApplicationCourseId,
    },
    commissionPlan: {
      existing: existingPlan,
      defaults: {
        location,
        tuitionFee: courseTuitionFee,
        commissionableTuitionFee: courseCommissionableTuitionFee,
        commissionType: "percentage",
        commissionRate: location === "offshore" ? offshoreRate : onshoreRate,
        commissionGstType: "exclusive",
        currency,
        status: "active",
        levelId,
        levelTitle: levelTitle || "",
        onshoreRate,
        offshoreRate,
        onshoreBonus: toNumber(
          course.onshore_bonus ?? eduAppCourse.onshore_bonus,
        ),
        offshoreBonus: toNumber(
          course.offshore_bonus ?? eduAppCourse.offshore_bonus,
        ),
        applicationCourseId: planApplicationCourseId,
        providerName: provider.name || "",
        courseName: courseName || "",
        clientName: clientName || "",
      },
    },
    header: {
      title: clientName || "Application Detail",
      badge: formatAppBadge(applicationId, application.created_at),
      subtext: [provider.name, courseName, intakeLabel, "Education"]
        .filter(Boolean)
        .join(" • "),
    },
    kpi: {
      courseTuitionFee,
      providerCommissionOnshore: onshoreRate || providerCommissionOnshore,
      providerCommissionOffshore: offshoreRate || providerCommissionOffshore,
      expectedCommission,
      outstandingCommission,
      courseTuitionFeeSublabel: "Total course value",
      providerCommissionSublabel: "Agreed commission rate",
      expectedCommissionSublabel: "Total commission we should earn",
      outstandingCommissionSublabel: "Remaining commission to receive",
      currency,
    },
    summary: {
      expectedCommission,
      expectedCommissionFormatted: formatCurrency(expectedCommission, currency),
      statusTitle,
      statusBadge: statusTitle.toUpperCase(),
      providerCommissionOnshore: onshoreRate || providerCommissionOnshore,
      providerCommissionOffshore: offshoreRate || providerCommissionOffshore,
      providerCommissionOnshoreLabel: formatPercent(
        onshoreRate || providerCommissionOnshore,
        2,
      ),
      providerCommissionOffshoreLabel: formatPercent(
        offshoreRate || providerCommissionOffshore,
        2,
      ),
      tuitionFee: courseTuitionFee,
      tuitionFeeFormatted: formatCurrency(courseTuitionFee, currency),
    },
    tuitionProgress: {
      courseTuitionFee,
      courseTuitionFeeFormatted: formatCurrency(courseTuitionFee, currency),
      paidTuitionFee,
      paidTuitionFeeFormatted: formatCurrency(paidTuitionFee, currency),
      remainingTuitionFee,
      remainingTuitionFeeFormatted: formatCurrency(
        remainingTuitionFee,
        currency,
      ),
      progressPct: tuitionProgressPct,
      progressLabel: `${tuitionProgressPct.toFixed(0)}% Paid`,
      footerText: `${tuitionProgressPct.toFixed(0)}% of tuition fee has been paid by the student`,
    },
    commissionProgress: {
      expectedCommission,
      expectedCommissionFormatted: formatCurrency(expectedCommission, currency),
      commissionReceived,
      commissionReceivedFormatted: formatCurrency(commissionReceived, currency),
      outstandingCommission,
      outstandingCommissionFormatted: formatCurrency(
        outstandingCommission,
        currency,
      ),
      progressPct: commissionProgressPct,
      progressLabel: `${commissionProgressPct.toFixed(0)}% Received`,
      footerText: `${commissionProgressPct.toFixed(0)}% of expected commission has been received`,
    },
    bonus: {
      expectedBonus,
      expectedBonusFormatted: formatCurrency(expectedBonus, currency),
      bonusReceived,
      bonusReceivedFormatted: formatCurrency(bonusReceived, currency),
      outstandingBonus,
      outstandingBonusFormatted: formatCurrency(outstandingBonus, currency),
      status: bonusStatus,
    },
    installments,
    payments,
    notes: commissionNotes.map(mapCommissionNoteRow),
    links: {
      client: `/clients/view-client-detail/${clientIdRaw}`,
      application: `/applications/view-education-application-detail/${applicationId}`,
    },
  };
}
